import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { computeFinance } from '@/lib/finance';
import { SERVICES, REGIONS } from '@/mocks/constants';

export default function Raporlar() {
  const { requests, quotes, jobs, appointments, payments, expenses, teams } = useAppStore();
  const navigate = useNavigate();
  const finance = computeFinance(jobs, payments, expenses);

  const videoReceived = requests.filter((r) => r.videoStatus === 'Video Geldi').length;
  const approved = quotes.filter((q) => q.status === 'Onaylandı').length;
  const rejected = quotes.filter((q) => q.status === 'Reddedildi').length;
  const completedJobs = jobs.filter((j) => j.status === 'Kapandı');
  const conversion = quotes.length > 0 ? Math.round((approved / quotes.length) * 100) : 0;

  const funnel = [
    { label: 'Yeni Talep', value: requests.length, path: '/teklif-talepleri' },
    { label: 'Video Geldi', value: videoReceived, path: '/teklif-talepleri' },
    { label: 'Teklif', value: quotes.length, path: '/teklifler' },
    { label: 'Onay', value: approved, path: '/teklifler' },
    { label: 'Randevu', value: appointments.length, path: '/randevu' },
    { label: 'Tamamlandı', value: completedJobs.length, path: '/tamamlanan-isler' },
  ];

  const quoteMetrics = [
    { label: 'Yeni Talep', value: requests.length, icon: 'ri-file-list-3-line', tone: 'bg-primary-50 text-primary-600' },
    { label: 'Video Geldi', value: videoReceived, icon: 'ri-video-line', tone: 'bg-accent-100 text-accent-700' },
    { label: 'Teklif Hazırlandı', value: quotes.length, icon: 'ri-file-text-line', tone: 'bg-primary-50 text-primary-600' },
    { label: 'Onaylanan', value: approved, icon: 'ri-checkbox-circle-line', tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Reddedilen', value: rejected, icon: 'ri-close-circle-line', tone: 'bg-red-50 text-red-600' },
    { label: 'Dönüşüm Oranı', value: `${conversion}%`, icon: 'ri-line-chart-line', tone: 'bg-amber-50 text-amber-600' },
  ];

  const serviceRows = SERVICES.map((s) => {
    const reqCount = requests.filter((r) => r.service === s).length;
    const serviceJobs = jobs.filter((j) => j.service === s);
    const done = serviceJobs.filter((j) => j.status === 'Kapandı').length;
    const revenue = serviceJobs.reduce((sum, j) => sum + j.jobValue, 0);
    return { service: s, reqCount, done, revenue };
  }).filter((r) => r.reqCount > 0 || r.revenue > 0);

  const regionRows = REGIONS.map((r) => {
    const reqCount = requests.filter((x) => x.region === r).length;
    const regionJobs = jobs.filter((j) => j.location.includes(r.split(' ')[0]) || j.location.includes(r));
    const done = regionJobs.filter((j) => j.status === 'Kapandı').length;
    const revenue = regionJobs.reduce((sum, j) => sum + j.jobValue, 0);
    return { region: r, reqCount, done, revenue };
  });

  const financeMetrics = [
    { label: 'İş Bedeli / Ciro', value: finance.ciro, tone: 'text-foreground-900' },
    { label: 'Tahsilat', value: finance.collected, tone: 'text-emerald-600' },
    { label: 'Açık Alacak', value: finance.openReceivable, tone: 'text-amber-600' },
    { label: 'Gider', value: finance.expense, tone: 'text-red-600' },
    { label: 'Net Nakit Sonucu', value: finance.netCash, tone: 'text-accent-700' },
  ];

  const teamActivity = teams.map((t) => ({
    name: t.name,
    count: jobs.filter((j) => j.team === t.name && j.status === 'Kapandı').length,
  }));

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
          Yönetim · Demo Veri
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Raporlar</h1>
        <p className="mt-1 text-sm text-foreground-500">İşletmenin genel performansına dair özet.</p>
      </div>

      {/* Operation funnel */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white p-5">
        <h2 className="font-heading text-[15px] font-bold text-foreground-950">Operasyon Hunisi</h2>
        <p className="mt-0.5 text-xs text-foreground-400">Her aşama tıklanabilir</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          {funnel.map((f, i) => (
            <div key={f.label} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(f.path)}
                className="flex w-full flex-col items-center rounded-lg bg-primary-950 px-3 py-3 text-center transition-colors hover:bg-primary-800"
              >
                <span className="font-heading text-2xl font-bold text-white">{f.value}</span>
                <span className="mt-0.5 text-[11px] font-semibold text-primary-300">{f.label}</span>
              </button>
              {i < funnel.length - 1 && (
                <i className="ri-arrow-down-s-line shrink-0 text-foreground-300 sm:rotate-[-90deg]" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Teklif performansı */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <div className="border-b border-background-100 px-5 py-4">
          <h2 className="font-heading text-[15px] font-bold text-foreground-950">Teklif Performansı</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-6">
          {quoteMetrics.map((m) => (
            <div key={m.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${m.tone}`}>
                <i className={`${m.icon} text-lg`} />
              </div>
              <div className="mt-3 font-heading text-xl font-bold text-foreground-950">{m.value}</div>
              <div className="mt-0.5 text-xs font-semibold text-foreground-500">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Hizmet performansı */}
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          <div className="border-b border-background-100 px-5 py-4">
            <h2 className="font-heading text-[15px] font-bold text-foreground-950">Hizmet Performansı</h2>
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-100 text-xs font-semibold uppercase text-foreground-400">
                <th className="px-5 py-3">Hizmet</th>
                <th className="px-3 py-3">Talep</th>
                <th className="px-3 py-3">Onaylı İş</th>
                <th className="px-5 py-3 text-right">Gelir</th>
              </tr>
            </thead>
            <tbody>
              {serviceRows.map((r) => (
                <tr key={r.service} className="border-b border-background-100">
                  <td className="px-5 py-3 font-semibold text-foreground-800">{r.service}</td>
                  <td className="px-3 py-3 text-foreground-600">{r.reqCount}</td>
                  <td className="px-3 py-3 text-foreground-600">{r.done}</td>
                  <td className="px-5 py-3 text-right font-semibold text-foreground-900">{formatTL(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bölge performansı */}
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          <div className="border-b border-background-100 px-5 py-4">
            <h2 className="font-heading text-[15px] font-bold text-foreground-950">Bölge Performansı</h2>
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-100 text-xs font-semibold uppercase text-foreground-400">
                <th className="px-5 py-3">Bölge</th>
                <th className="px-3 py-3">Talep</th>
                <th className="px-3 py-3">Onaylı İş</th>
                <th className="px-5 py-3 text-right">Gelir</th>
              </tr>
            </thead>
            <tbody>
              {regionRows.map((r) => (
                <tr key={r.region} className="border-b border-background-100">
                  <td className="px-5 py-3 font-semibold text-foreground-800">{r.region}</td>
                  <td className="px-3 py-3 text-foreground-600">{r.reqCount}</td>
                  <td className="px-3 py-3 text-foreground-600">{r.done}</td>
                  <td className="px-5 py-3 text-right font-semibold text-foreground-900">{formatTL(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Finans performansı */}
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <h2 className="font-heading text-[15px] font-bold text-foreground-950">Finans Performansı</h2>
          <div className="mt-4 space-y-2.5">
            {financeMetrics.map((m) => (
              <div key={m.label} className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
                <span className="text-sm font-semibold text-foreground-600">{m.label}</span>
                <span className={`font-heading text-base font-bold ${m.tone}`}>{formatTL(m.value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Ekip aktivitesi */}
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <h2 className="font-heading text-[15px] font-bold text-foreground-950">Ekip Aktivitesi</h2>
          <p className="mt-0.5 text-xs text-foreground-400">Tamamlanan iş sayısı (rekabet sıralaması değil)</p>
          <div className="mt-4 space-y-3">
            {teamActivity.map((t) => {
              const max = Math.max(...teamActivity.map((x) => x.count), 1);
              return (
                <div key={t.name} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-sm font-semibold text-foreground-700">{t.name}</span>
                  <div className="h-5 flex-1 overflow-hidden rounded-sm bg-background-100">
                    <div
                      className="flex h-full items-center justify-end rounded-sm bg-primary-500 pr-2"
                      style={{ width: `${Math.max(8, Math.round((t.count / max) * 100))}%` }}
                    >
                      <span className="text-[11px] font-bold text-white">{t.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}